// Picks the AuthAdapter implementation at boot.
//
//   VITE_AUTH_ADAPTER=mock      (default)  → MockAuthAdapter
//   VITE_AUTH_ADAPTER=firebase             → FirebaseAuthAdapter
//
// The default-to-mock policy means: a misconfigured deploy lands on
// the mock (visible to anyone using the app — login is "type any
// email") rather than on a half-wired Firebase that crashes. The
// production Hosting target sets `firebase` explicitly. There is NO
// runtime auto-detection — explicit configuration only.
//
// IMPORTANT: BOTH adapters are loaded via dynamic import. If the
// MockAuthAdapter were eagerly imported here, Vite would bundle its
// dependencies (TenantNamespaceSchema from @csai/core/browser) into
// the MAIN entry chunk. The Firebase chunks also depend on
// @csai/core/browser, so they'd back-import from the main chunk —
// which is itself awaiting `selectAuthAdapter` to resolve at top
// level. Top-level await in main.tsx + this circular ESM dep =
// **deadlock** (the dynamic-imported chunks load successfully but
// their evaluation blocks waiting for the main chunk that's blocked
// waiting for them). Symptom: blank #root, no exceptions, no console
// errors. Verified by adding [moduleName] top-level console.logs and
// seeing FirebaseAuthAdapter never evaluates.
//
// Keeping BOTH adapters dynamic puts @csai/core/browser into a
// shared chunk that neither side awaits at the top level, breaking
// the cycle.
import type { AuthAdapter } from "./types";

export async function selectAuthAdapter(): Promise<AuthAdapter> {
  const choice = import.meta.env.VITE_AUTH_ADAPTER ?? "mock";
  if (choice === "firebase") {
    const [{ FirebaseAuthAdapter }, { getFirebaseAuth }, resolverModule] =
      await Promise.all([
        import("./FirebaseAuthAdapter"),
        import("./firebase/client"),
        import("./firebase/tenantResolver"),
      ]);
    const auth = getFirebaseAuth();
    // S-409 — resolve the login tenant by EMAIL via the admin API, falling
    // back to the domain-based static map (for platform admins / unknown
    // emails). This makes the invited tenant authoritative regardless of the
    // user's email domain.
    const baseUrl = import.meta.env.VITE_ADMIN_API_BASE ?? "/api/admin";
    const tenantResolver = new resolverModule.AdminLookupTenantResolver({
      baseUrl,
      domainResolver: new resolverModule.StaticEnvTenantResolver(),
    });
    return new FirebaseAuthAdapter({ auth, tenantResolver });
  }
  const { MockAuthAdapter } = await import("./MockAuthAdapter");
  return new MockAuthAdapter();
}
